import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import type { Exercise } from '@jackedlogs/shared';
import type { LoggerCatalogAdapter } from './hostDeps';

// Logger exercise catalog: cache-first load once per modal-open + a
// background SWR refresh (so a cold gym boot shows last-known rows before
// the network returns, and the plate calc can read defaultBarKg/barLoading).
// Returns [catalog, setCatalog] — the rest-default editor patches the
// in-memory copy after a PATCH so the next set picks up the new default
// without a reload. Extracted from useLoggerScreen (size budget).
export function useLoggerCatalog(
  visible: boolean,
  catalogAdapter: LoggerCatalogAdapter,
): readonly [Exercise[] | null, Dispatch<SetStateAction<Exercise[] | null>>] {
  const [catalog, setCatalog] = useState<Exercise[] | null>(null);
  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    void (async () => {
      const cached = await catalogAdapter.loadCached();
      if (!cancelled && cached !== null) setCatalog(cached);
      try {
        const fresh = await catalogAdapter.fetchAndPersist();
        if (!cancelled) setCatalog(fresh);
      } catch {
        // best-effort; cached covers the offline case
      }
    })();
    return () => {
      cancelled = true;
    };
    // `catalogAdapter` is host-injected and stable per mount (the RW-18 seam):
    // adding it would re-run this on every host render and turn a
    // load-once-per-modal-open into a refetch loop. Driven by `visible` on
    // purpose. Became visible only when packages/ui got a linter — CN-UI-LINT.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  return [catalog, setCatalog] as const;
}
